import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import CgPopover from './CgPopover';

/**
 * Small info icon explaining that a quarter's "average price" is an
 * approximation — we compute the mean of each published weekly average
 * price rather than the true volume-weighted average (total paid ÷ total
 * certificates traded), which we don't have data for.
 *
 * Render this next to any Q{n} Average value that comes from a quarter
 * whose price status is not yet 'confirmed'.
 */
const ProjectedAverageInfo = ({ className = '', iconClassName = '' }) => {
  const { t } = useTranslation('common');
  const trigger = (
    <span
      className={`inline-flex items-center cursor-help ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <FontAwesomeIcon
        icon={faCircleInfo}
        className={`text-amber-500 text-[10px] ${iconClassName}`}
      />
    </span>
  );

  const content = (
    <div className="max-w-[280px] text-xs text-gray-700 space-y-1.5">
      <p className="font-semibold text-gray-800">
        {t('common:projectedAverage.title', 'Approximate average')}
      </p>
      <p>
        {t(
          'common:projectedAverage.body',
          "Computed by averaging each week's published average price. The quarter isn't closed yet, so this is an estimate rather than a confirmed figure.",
        )}
      </p>
      <p className="text-gray-500">
        {t(
          'common:projectedAverage.footnote',
          "A truly accurate average would be volume-weighted (total paid ÷ total certificates traded), but weekly trade-volume data isn't available to us — averaging the weekly averages is the best estimate we can offer.",
        )}
      </p>
    </div>
  );

  return (
    <CgPopover
      triggerElement={trigger}
      popoverContent={content}
      position="top"
      trigger="hover"
      sideOffset={4}
    />
  );
};

export default ProjectedAverageInfo;
